import { Component, DestroyRef, inject, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, ParamMap, Router, RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { combineLatest, distinctUntilChanged, map, Observable, of, switchMap, take, tap } from 'rxjs';

import { MessageService } from '@openng/optimus-ui/api';
import { ToastModule } from '@openng/optimus-ui/toast';
import { CardModule } from '@openng/optimus-ui/card';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';
import { JiraTokenPromptComponent, JiraTokenPromptService } from '@tmt/jira';

import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { SidebarComponent } from 'src/app/components/sidebar/sidebar.component';
import { NavigationHeaderComponent } from 'src/app/components/navigation-header/navigation-header.component';
import { ThemeService } from 'src/app/services/theme.service';
import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { AuthenticationMutations } from 'src/app/graphql/mutations/authentication.mutation';
import { GraphqlUserDataOutput } from 'src/app/graphql/models/output/user-data.output.model';
import { convertGraphqlUserDataOutputToUserData } from 'src/app/graphql/mappers/user-data.mappers';
import { ImportProgressBannerComponent } from 'src/app/components/import-progress-banner/import-progress-banner.component';
import { LaunchdarklyService } from 'src/app/services/launchdarkly.service';
import { BrowserAnalyticsService } from 'src/app/services/browser-analytics.service';
import { UpdateService } from 'src/app/services/update.service';

@Component({
	selector: 'app-root',
	imports: [
		RouterOutlet,
		NavigationHeaderComponent,
		SidebarComponent,
		AsyncPipe,
		ToastModule,
		CardModule,
		ButtonModule,
		ProgressSpinnerModule,
		ImportProgressBannerComponent,
		JiraTokenPromptComponent,
	],
	templateUrl: './app.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
	authService = inject(AuthService);

	private route = inject(ActivatedRoute);

	private router = inject(Router);

	private messageService = inject(MessageService);

	private themeService = inject(ThemeService);

	private apolloClientService = inject(ApolloClientService);

	private jiraTokenPromptService = inject(JiraTokenPromptService);

	private launchdarklyService = inject(LaunchdarklyService);

	private browserAnalyticsService = inject(BrowserAnalyticsService);

	public updateService = inject(UpdateService);

	@ViewChild(SidebarComponent) sidebar?: SidebarComponent;

	@ViewChild(NavigationHeaderComponent) navigationHeader?: NavigationHeaderComponent;

	title = 'Analyzer';

	public isProcessingAuthentication = signal<boolean>(false);

	/** Whether the first-login "connect your Jira account" prompt is open. */
	public jiraTokenPromptVisible = signal<boolean>(false);

	private destroyRef = inject(DestroyRef);

	private processingToken = false;

	params$: Observable<ParamMap> = this.route.queryParamMap;

	/**
	 * Authentication Flow - Bare Bones Version
	 *
	 * This flow handles one-time token authentication with the following steps:
	 * 1. Check if user has a session (stored in localStorage)
	 * 2. If no session, check for token in URL query params
	 * 3. If token exists, consume it via GraphQL mutation to get session
	 * 4. Store session data in localStorage
	 * 5. If no token and no session, redirect to external auth page
	 *
	 * Key Points:
	 * - Uses combineLatest to react to both URL params and userData changes
	 * - Prevents duplicate token consumption with processingToken flag
	 * - Parses token from URLSearchParams (not Angular's params$) to handle edge cases
	 * - Template conditionally shows app content only when session exists
	 */
	ngOnInit(): void {
		this.themeService.getTheme();
		this.browserAnalyticsService.start(this.router, this.destroyRef);

		// Scroll to top on every route navigation and handle sidebar visibility
		this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
			if (event instanceof NavigationEnd) {
				window.scrollTo(0, 0);
				document.documentElement.scrollTop = 0;
				document.body.scrollTop = 0;

				// Collapse sidebar for routes that request it
				const routeData = this.route.firstChild?.snapshot.data;
				if (routeData?.['hideSidebar'] && this.sidebar) {
					this.sidebar.sidebarVisible.set(false);
				}
			}
		});

		combineLatest({
			params: this.params$,
			userData: this.authService.userData$,
		})
			.pipe(
				// Only re-run when sessionID actually changes
				distinctUntilChanged((prev, curr) => prev.userData.sessionID === curr.userData.sessionID),
				switchMap(data => {
					// Parse token from URL (URLSearchParams is more reliable than params$)
					const urlParams = new URLSearchParams(window.location.search);
					const token = urlParams.get(environment.oneTimeTokenName);

					// No session? Try to authenticate
					if (!data.userData.sessionID && token) {
						// Prevent duplicate token consumption
						if (this.processingToken) {
							return of(false);
						}
						this.processingToken = true;
						this.isProcessingAuthentication.set(true);

						// Handle auth failure case
						if (token === 'noAuth') {
							this.messageService.add({
								severity: 'error',
								summary: 'Error',
								detail: 'Failed to sign in. No authentication token provided.',
							});
							this.processingToken = false;
							return of(false);
						}

						// Consume the one-time token to get session
						return this.apolloClientService.authLayerClient.pipe(
							take(1),
							switchMap(client =>
								client.mutate<{ consumeOneTimeToken: GraphqlUserDataOutput }>({
									mutation: AuthenticationMutations.consumeOneTimeToken,
									variables: { token: token },
								}),
							),
							map(result => (result.data?.consumeOneTimeToken ? convertGraphqlUserDataOutputToUserData(result.data.consumeOneTimeToken) : undefined)),
							map(data => {
								if (data?.sessionID) {
									// Store session, then return the user to the page they opened before
									// signing in (e.g. a shared ?env= link).
									this.authService.updateSession(data);
									const returnUrl = this.authService.consumePostLoginUrl();
									if (returnUrl) {
										void this.router.navigateByUrl(returnUrl);
									} else {
										this.authService.removeSpecificQueryParams(['sessionId', 'username', 'company', environment.oneTimeTokenName]);
									}
									this.processingToken = false;
								} else {
									this.messageService.add({
										severity: 'error',
										summary: 'Error',
										detail: 'Failed to retrieve user data. Please sign in again.',
									});
									this.processingToken = false;
								}
								return false;
							}),
							tap({
								error: (err: any) => {
									console.error('Error consuming token:', err);
									this.processingToken = false;
								},
							}),
						);
					}
					// Already have session, do nothing
					return of(false);
				}),
				tap(() => this.isProcessingAuthentication.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();

		// Check for updates, and reload page to ensure latest client version is installed if necessary.
		this.updateService
			.updatesAvailable$()
			.pipe(
				distinctUntilChanged(),
				tap(updateAvailable => {
					if (updateAvailable) {
						this.showUpdateNotice();
					}
				}),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe();

		// First-login Jira token prompt: when a fresh login establishes a sessionID, ask the shared
		// @tmt/jira prompt service whether to invite the user to set up a personal token (it checks the
		// permanent dismissal, once-per-session, and no-token-registered rules). The feature gate (the
		// personal-jira-token LaunchDarkly flag) is this app's concern, so we pass it in; it is
		// temporary and goes away at go-live, after which `true` can be passed. distinctUntilChanged on
		// the sessionID means a reload (same session) does not re-trigger the backend check.
		this.authService.userData$
			.pipe(
				map(userData => userData.sessionID ?? null),
				distinctUntilChanged(),
				switchMap(sessionId =>
					this.launchdarklyService.launchDarklyFlags$.pipe(
						take(1),
						switchMap(flags => this.jiraTokenPromptService.shouldShowForSession$(sessionId, !!flags['personal-jira-token'])),
					),
				),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(show => {
				if (show) this.jiraTokenPromptVisible.set(true);
			});
	}

	/** User chose "Create a token": close the prompt and go to the Settings page (Jira Integration). */
	public onJiraPromptCreate(): void {
		this.jiraTokenPromptVisible.set(false);
		void this.router.navigate(['/settings']);
	}

	/**
	 * User dismissed the prompt. `persist` is true only for "Don't remind me again" (stored so it
	 * never shows again on this browser); "Remind me later" just closes it and it returns next login.
	 * @param persist true to permanently suppress the prompt, false to show it again next login
	 */
	public onJiraPromptDismiss(persist: boolean): void {
		if (persist) this.jiraTokenPromptService.dismissPermanently();
		this.jiraTokenPromptVisible.set(false);
	}

	/**
	 * Shows a custom toast message that prompts for reloading when a new version is found.
	 */
	public showUpdateNotice() {
		this.messageService.add({
			key: 'reload',
			life: 20000,
		});
	}
}
